import React from 'react';
import { Form, Input, Checkbox, Tooltip, Alert } from '../../../../../components';
import { CopyOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { NameField } from '../../cloudAccountFields';
import { TestConnectionButton } from '../../cloudAccountFields';
import TagsSelector from '../../../../TagsSelector';
import { getPrivateDeploymentCloud } from '../../utils/cloudAccountHelpers';
import { isAWS } from '../../../../../constants';
import './AWSCloudAccount.scss';

/**
 * AWS Cloud Account - Edit Mode
 * Focused component for editing existing AWS cloud accounts
 */
export const AWSCloudAccountEdit = ({
    formData,
    errors,
    onChange,
    onTestConnection,
    testing,
    tested,
    externalId,
    onCopyExternalId,
    canEditVSphereCredentials,
    showEditVSphereCredentials,
    onToggleVSphereEdit,
    vsphereCredentials,
    onVSphereCredentialChange,
    tags,
    onTagsChange,
    userOrg,
}) => {
    const isAWSPrivateDeployment = isAWS(getPrivateDeploymentCloud());

    return (
        <>
            <NameField value={formData.name} onChange={onChange('name')} error={errors.name} />

            {/* Credentials Warning */}
            <Alert
                title="Only update credentials if they have changed."
                type="warning"
                showIcon
                icon={<QuestionCircleOutlined />}
                className="aws-credentials-alert"
            />

            {/* Role Field */}
            {!isAWSPrivateDeployment && (
                <Form.Item
                    label={
                        <span>
                            Role{' '}
                            <Tooltip title="Enter the Amazon Resource Name (ARN) associated with the role created to perform migration(s).">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    validateStatus={errors.username ? 'error' : ''}
                    help={errors.username}
                >
                    <Input
                        value={formData.username}
                        onChange={onChange('username')}
                        placeholder="Enter Role (optional)"
                        className={formData.username ? 'aws-modified-input' : ''}
                    />
                </Form.Item>
            )}

            {/* External ID Display */}
            <Form.Item
                label={
                    <span>
                        External ID{' '}
                        <Tooltip title="External ID is only needed if you create the role ARN manually.">
                            <QuestionCircleOutlined />
                        </Tooltip>
                    </span>
                }
            >
                <Input value={externalId} disabled cop className="aws-disabled-input" />
            </Form.Item>

            {/* Copy External ID Link */}
            <div className="aws-copy-section">
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        onCopyExternalId();
                    }}
                    className="aws-copy-link"
                >
                    <CopyOutlined className="aws-copy-icon" />
                    Copy External ID
                </a>
            </div>

            {/* Test Connection */}
            <TestConnectionButton
                onTest={onTestConnection}
                testing={testing}
                tested={tested}
                tooltip="As an option, you can test connection and role ARN before you continue."
                required={false}
            />

            {/* vSphere Credentials Edit */}
            {canEditVSphereCredentials && (
                <>
                    <Form.Item>
                        <Checkbox
                            checked={showEditVSphereCredentials}
                            onChange={(e) => onToggleVSphereEdit(e.target.checked)}
                        >
                            Edit Source vSphere Environment Credentials
                        </Checkbox>
                    </Form.Item>

                    {showEditVSphereCredentials && (
                        <div className="aws-vsphere-credentials-box">
                            <h5 className="aws-vsphere-credentials-heading">Edit Credentials</h5>

                            <Form.Item label="Username">
                                <Input
                                    value={vsphereCredentials.username}
                                    onChange={(e) => onVSphereCredentialChange('username', e.target.value)}
                                    placeholder="Enter username"
                                />
                            </Form.Item>

                            <Form.Item label="Password">
                                <Input.Password
                                    value={vsphereCredentials.password}
                                    onChange={(e) => onVSphereCredentialChange('password', e.target.value)}
                                    placeholder="Enter password"
                                />
                            </Form.Item>
                        </div>
                    )}
                </>
            )}

            {/* Tags */}
            <Form.Item label="Tags">
                <TagsSelector value={tags} onChange={onTagsChange} userOrg={userOrg} />
            </Form.Item>
        </>
    );
};
